import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'react-bootstrap';
import { useInstitutionPolicy } from '@/shared/hooks/useInstitutionPolicy';
import { renderMarkdown } from '@/shared/utils/markdown';

function PolicyBlock({ heading, guidance }) {
    return (
        <section aria-label={heading} className="mb-3">
            <h3 className="h6">{heading}</h3>
            <div
                dangerouslySetInnerHTML={{
                    __html: renderMarkdown(guidance.text),
                }}
            />
            {guidance.link && (
                <a
                    href={guidance.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {guidance.link.label}
                </a>
            )}
        </section>
    );
}

PolicyBlock.propTypes = {
    heading: PropTypes.string.isRequired,
    guidance: PropTypes.shape({
        text: PropTypes.string,
        link: PropTypes.shape({
            label: PropTypes.string,
            url: PropTypes.string,
        }),
    }).isRequired,
};

/**
 * Replaces the listing form when the poster says the item can store data.
 *
 * Devices that hold data go through the institution's own IT process, never
 * through a swap between departments, so this panel offers no way forward
 * except that process. Both texts and links come from the poster's
 * institution policy and are rendered through renderMarkdown.
 */
function DataDeviceBlock({ institution }) {
    const { policy, loading, error } = useInstitutionPolicy(institution);

    return (
        <Alert variant="danger" className="afe-data-device-block">
            <Alert.Heading as="h2" className="h5">
                This item can&apos;t be listed here
            </Alert.Heading>
            <p>
                Devices that can store data must not be swapped between
                departments. They go through your institution&apos;s IT process
                so the data is removed and the equipment is handled correctly.
            </p>
            {loading && (
                <p className="mb-0" role="status">
                    Loading your institution&apos;s IT process.
                </p>
            )}
            {error && (
                <p className="mb-0">
                    Could not load your institution&apos;s IT process. Contact
                    its IT department before the device moves.
                </p>
            )}
            {policy && policy.dataDevices && (
                <PolicyBlock
                    heading="Data-bearing devices"
                    guidance={policy.dataDevices}
                />
            )}
            {policy && policy.technologyDisposal && (
                <PolicyBlock
                    heading="Technology disposal"
                    guidance={policy.technologyDisposal}
                />
            )}
            <p className="mb-0">
                Change your answer above if this item does not store data, such
                as a monitor, keyboard, or mouse.
            </p>
        </Alert>
    );
}

DataDeviceBlock.propTypes = { institution: PropTypes.string };

export default DataDeviceBlock;
